import * as React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import {
  BarChart3,
  Database,
  History,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Receipt,
  Users,
  Wand2,
  Webhook,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { signOut, useSession } from '@/lib/auth';

const nav = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/buckets', label: 'Buckets', icon: Database },
  { to: '/transform', label: 'Transform', icon: Wand2 },
];

const settingsNav = [
  { to: '/settings/tokens', label: 'Tokens', icon: KeyRound },
  { to: '/settings/team', label: 'Team', icon: Users },
  { to: '/settings/webhooks', label: 'Webhooks', icon: Webhook },
  { to: '/settings/audit', label: 'Audit', icon: History },
  { to: '/settings/usage', label: 'Usage', icon: BarChart3 },
  { to: '/settings/plan', label: 'Plan', icon: Receipt },
];

export function Layout() {
  const session = useSession();
  const navigate = useNavigate();

  if (session.isPending) {
    return (
      <div className="grid h-full place-items-center">
        <Skeleton className="h-6 w-32" />
      </div>
    );
  }
  const user = session.data?.user as
    | { id: string; email: string; role?: string }
    | undefined;
  if (!user) {
    return (
      <div className="grid h-full place-items-center">
        <p className="text-muted-foreground">
          You need to{' '}
          <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
            sign in
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <aside className="flex w-60 flex-col border-r bg-muted/30">
        <div className="px-5 py-5 text-lg font-semibold tracking-tight">Storeflare</div>
        <nav className="flex-1 space-y-4 px-2">
          <NavSection items={nav} />
          <div>
            <div className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Settings
            </div>
            <NavSection items={settingsNav} />
          </div>
        </nav>
        <Separator />
        <div className="p-3 text-sm">
          <div className="truncate font-medium">{user.email}</div>
          {user.role ? (
            <div className="text-xs capitalize text-muted-foreground">{user.role}</div>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-7 px-2 text-xs text-muted-foreground"
            onClick={async () => {
              await signOut();
              toast.success('Signed out');
              navigate('/login');
            }}
          >
            <LogOut className="mr-1.5" />
            Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

function NavSection({
  items,
}: {
  items: { to: string; label: string; icon: React.ComponentType<{ className?: string }> }[];
}) {
  return (
    <div className="space-y-0.5">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </div>
  );
}
